import React from "react";
import moment from "moment";
import { IAddOn } from "@eagerdog/interfaces";
import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";
import styles from "./StoreItem.module.scss";

interface IProps {
  item: IAddOn;
  cartQuantity: number;
  onAddToCart: (itemId: string, quantity: number) => void;
  isAdmin: boolean;
  onEdit?: (item: IAddOn) => void;
  onDelete?: (item: IAddOn) => void;
  currency: string;
}

const StoreItem: React.FC<IProps> = ({ item, cartQuantity, onAddToCart, isAdmin, onEdit, onDelete, currency }) => {
  const getExpiryInfo = () => {
    if (!item.expires_at) return null;

    const expiryDate = moment(item.expires_at);
    const now = moment();
    const daysUntilExpiry = expiryDate.diff(now, 'days');

    if (daysUntilExpiry < 0) {
      return { text: "Expired" };
    }

    if (daysUntilExpiry < 10) {
      return { 
        text: `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}` 
      };
    }

    return { 
      text: `Expires on ${expiryDate.format('MMMM Do, YYYY @ h:mmA')}` 
    };
  };

  const expiryInfo = getExpiryInfo();

  return (
    <div className={styles.storeItem}>
      {/* Image Section */}
      <div className={styles.itemImage}>
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} />
        ) : (
          <div className={styles.imagePlaceholder}>
            <i className="fa fa-image"></i>
            <span>No Image</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className={styles.itemContent}>
        {isAdmin && (
          <div className={styles.adminIcons}>
            {onEdit && (
              <button className={styles.iconButton} onClick={() => onEdit(item)} title="Edit item">
                <Icon type="edit" />
              </button>
            )}
            {onDelete && (
              <button className={styles.iconButton} onClick={() => onDelete(item)} title="Delete item">
                <Icon type="delete" />
              </button>
            )}
          </div>
        )}
        
        <div className={styles.itemDetails}>
          {item.category && <span className={styles.category}>{item.category}</span>}
          <h3 className={styles.title}>{item.title}</h3>
          {expiryInfo && (
            <div className={styles.expiryInfo}>
              <span>{expiryInfo.text}</span>
            </div>
          )}
          {item.description && (
            <div 
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          )}
        </div>
        
        <div className={styles.itemFooter}>
          <div className={styles.price}>${item.fee.toFixed(2)} {currency}</div>
          <div className={styles.itemActions}>
            {cartQuantity === 0 ? (
              <Button onClick={() => onAddToCart(item._id, 1)} className={styles.addButton}>
                Add to Cart
              </Button>
            ) : (
              <div className={styles.quantityControls}>
                <button onClick={() => onAddToCart(item._id, cartQuantity - 1)}>−</button>
                <span>{cartQuantity}</span>
                <button onClick={() => onAddToCart(item._id, cartQuantity + 1)}>+</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreItem;

